import React, { useCallback, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import SectionScreen from '../SectionScreen'
import { useClientDetails, useSectionEditing } from '../ClientDetailsFormContext'
import { useGroupSearch, useGroupTypeSearch } from '../../../../../api/groups'
import { useModifyClientGroupsMutation } from '../../../../../api/clients'
import SectionHeader from '../../shared/SectionHeader'
import PersonalSpace from '../../shared/PersonalSpace'
import EditButton from '../EditButton'
import ClientTagDisplay from './ClientTagDisplay'

const useStyles = makeStyles((theme) => ({
  tagsSection: {
    position: 'relative',
    padding: '10px'
  }
}))

const useClientTags = (client) => {
  const groupsQuery = useMemo(() => ({
    filters: {
      membershipLevelTypeIds: [201],
      memberLevelIds: [client.clientId]
    },
    includes: {
      groupType: true
    }
  }), [client.clientId])

  const groupTypesQuery = useMemo(() => ({
    filters: {
      memberLevelTypeIds: [201]
    },
    includes: {
      groups: true,
      groupTypes: true,
      groupMemberTypes: true
    }
  }), [])

  const { data: groups, isLoading: groupsLoading, refetch } = useGroupSearch(groupsQuery)
  const { data: schema, isLoading: schemaLoading } = useGroupTypeSearch(groupTypesQuery)

  const groupTypesWithAssignments = useMemo(() => {
    if (groupsLoading || schemaLoading) return []

    return schema.data.map(groupType => {
      return {
        ...groupType,
        assigned: groups.data.find(x => x.groupType.groupTypeId === groupType.groupTypeId),
        options: groupType.groups.map(g => ({
          label: g.longName,
          disabled: false,
          displayLabel: g.longName,
          value: g.groupId,
          payload: {}
        }))
      }
    })
  }, [groupsLoading, schemaLoading, schema, groups])

  return {
    groups: groups?.data,
    schema: schema?.data,
    data: groupTypesWithAssignments,
    isLoading: groupsLoading || schemaLoading,
    refetch
  }
}

function TagsSection () {
  const classes = useStyles()
  const { client, editSection } = useClientDetails()
  const editing = useSectionEditing('tags')
  const { data, isLoading: groupsLoading, refetch } = useClientTags(client)
  const formRef = useRef()
  const { mutateAsync: modifyGroups } = useModifyClientGroupsMutation()
  const onSave = useCallback(async () => {
    await modifyGroups({
      clientId: client.clientId,
      groups: formRef.current.formValues.groups
    })
    await refetch()
  }, [client, formRef, refetch, modifyGroups])

  if (groupsLoading) {
    return <div>Loading</div>
  }

  return (
    <SectionScreen sectionName='tags' className={classes.tagsSection}>
      <SectionHeader text='Tag Assignments'>
        <div>
          <EditButton
            policy='admin_edit_client_tags'
            editing={editing}
            onClick={() => editSection({
              section: 'tags',
              saveDescription: 'Save Tag Assignments',
              onSave
            })}
          />
        </div>
      </SectionHeader>
      <ClientTagDisplay ref={formRef} groups={data} editing={editing} />
      <PersonalSpace />
    </SectionScreen>
  )
}

export default TagsSection
